import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CardQuestLocation from "../../components/common/Card/CardQuestLocation";
import Search from "../../components/common/Field/Search";
import LoadingCommon from "../../components/common/LoadingStatus/LoadingCommon";
import images from "../../images";
import { useLazyGetQuestListPerLocationMainQuery } from "../../services/apiSlice";
import { getSmartLocation } from "../../Utils/helper";

const AllQuests = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [location, setLocation] = useState({
    latitude: null,
    longitude: null,
    loading: true,
  });
  const [allQuests, setAllQuests] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const [triggerGetQuests, { isFetching }] =
    useLazyGetQuestListPerLocationMainQuery();

  const observer = useRef();
  const lastQuestElementRef = useCallback(
    (node) => {
      if (isFetching) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [isFetching, hasMore]
  );

  useEffect(() => {
    const fetchLocation = async () => {
      try {
        const loc = await getSmartLocation({ gpsTimeout: 3000 });
        setLocation({ ...loc, loading: false });
      } catch (error) {
        console.error("Location error:", error);
        setLocation({
          latitude: 1.3521,
          longitude: 103.8198,
          loading: false,
        });
      }
    };
    fetchLocation();
  }, []);

  useEffect(() => {
    if (!location.latitude || !location.longitude) return;

    const fetchData = async () => {
      try {
        const result = await triggerGetQuests({
          latitude: location.latitude,
          longitude: location.longitude,
          limit: 15,
          page: page,
          search: searchQuery,
        }).unwrap();

        const newQuests = result.data || [];

        setAllQuests((prev) => {
          if (page === 1) return newQuests.filter((q) => q.business);
          return [...prev, ...newQuests.filter((q) => q.business)];
        });

        if (newQuests.length < 15) {
          setHasMore(false);
        } else {
          setHasMore(true);
        }
      } catch (error) {
        console.error("Failed to fetch quests:", error);
      }
    };

    fetchData();
  }, [
    location.latitude,
    location.longitude,
    page,
    searchQuery,
    triggerGetQuests,
  ]);

  const handleSearchChange = (query) => {
    setSearchQuery(query);
    setPage(1);
    setHasMore(true);
  };

  return (
    <div className="section-content">
      {page === 1 && (
        <LoadingCommon statusLoading={isFetching || location.loading} />
      )}

      <div className="flex flex-col gap-4">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 flex items-center justify-center"
        >
          <img
            src={images.ArrowLeftIconBlue || images.BackIcon}
            alt="Back"
            className="w-10 h-10 object-contain"
          />
        </button>

        <Search
          className=""
          placeholder={t("base.search") || "Search"}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          showFavorites={false}
        />
      </div>

      <div className="flex flex-col w-full mb-5 space-y-6 mt-6">
        <h1 className="text-primary font-[Figtree] text-xl font-bold not-italic leading-none">
          All Quests
        </h1>

        <div className="grid grid-cols-3 gap-x-3 gap-y-6 w-full">
          {allQuests.map((quest, index) => {
            const isLastElement = allQuests.length === index + 1;

            return (
              <div
                key={`${quest.id}-${index}`}
                ref={isLastElement ? lastQuestElementRef : null}
                className="flex justify-center"
              >
                <CardQuestLocation
                  businessName={quest.business?.company_name || ""}
                  locationId={quest.location?.id}
                  isFavorited={quest.is_favorite}
                  businessAvatar={quest.business?.avatar_data?.file_path || ""}
                  thumnail={quest.quest_businesses}
                  address={quest.location?.name}
                  handleNavigateToDetails={() =>
                    navigate(`/quest/location/${quest.location?.id}`)
                  }
                />
              </div>
            );
          })}
        </div>

        {isFetching && page > 1 && (
          <div className="w-full flex justify-center py-4">
            <div className="w-6 h-6 border-2 border-[#253FF6] border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {!isFetching && !location.loading && allQuests.length === 0 && (
          <div className="text-center text-gray-400 mt-10">
            No quests found.
          </div>
        )}
      </div>
    </div>
  );
};

export default AllQuests;
